import { BlockStack, Card, Select, Text } from "@shopify/polaris"
import React, { useCallback, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useShowcaseThemeLayout } from "./ShowcaseThemeLayoutContext"
import {
  type ShowcaseOrderLookupWidthPercent,
  clampShowcaseWidthToColumnCount,
  getShowcaseTopColumnCount,
} from "./themeLayoutShowcaseDraft"

const isWidthOptionValue = (v: string): v is ShowcaseOrderLookupWidthPercent =>
  v === "33" || v === "50" || v === "67" || v === "100"

/** Showcase 样式 Tab：Width 可选项由「内容」里勾选启用的列数决定（非槽位总数） */
const ThemeLayoutLeftStyleShowcaseTabPanel = () => {
  const { t } = useTranslation("tracking_page")
  const { draft, setDraft } = useShowcaseThemeLayout()

  const columnCount = useMemo(
    () => getShowcaseTopColumnCount(draft.top_section_images),
    [draft.top_section_images],
  )

  useEffect(() => {
    const nextWidth = clampShowcaseWidthToColumnCount(columnCount, draft.width)

    if (nextWidth === draft.width) {
      return
    }

    setDraft((prev) => ({ ...prev, width: nextWidth }))
  }, [columnCount, draft.width, setDraft])

  const widthOptions = useMemo(() => {
    if (columnCount >= 3) {
      return [{ label: t("themeLayoutShowcaseWidth33"), value: "33" }]
    }
    if (columnCount === 2) {
      return [
        { label: t("themeLayoutShowcaseWidth33"), value: "33" },
        { label: t("themeLayoutShowcaseWidth50"), value: "50" },
        { label: t("themeLayoutShowcaseWidth67"), value: "67" },
      ]
    }
    return [{ label: t("themeLayoutShowcaseWidth100"), value: "100" }]
  }, [columnCount, t])

  const cornerRadiusOptions = useMemo(
    () => [
      { label: t("themeLayoutBackdropCornerRadiusSharp"), value: "SHARP" },
      { label: t("themeLayoutBackdropCornerRadiusStandard"), value: "STANDARD" },
      { label: t("themeLayoutBackdropCornerRadiusRounded"), value: "ROUNDED" },
    ],
    [t],
  )

  const handleWidthChange = useCallback(
    (value: string) => {
      if (!isWidthOptionValue(value)) {
        return
      }
      setDraft((prev) => ({ ...prev, width: value }))
    },
    [setDraft],
  )

  const handleCornerRadiusChange = useCallback(
    (value: string) => {
      if (value !== "SHARP" && value !== "STANDARD" && value !== "ROUNDED") {
        return
      }
      setDraft((prev) => ({ ...prev, corner_radius: value }))
    },
    [setDraft],
  )

  return (
    <BlockStack gap="400">
      <Card>
        <Text variant="headingMd" as="h3">
          {t("OrderLookupWidget")}
        </Text>
        <div className="mt-1">
          <Text as="p" tone="subdued">
            {t("OrderLookupWidgetDescription")}
          </Text>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <Select
            label={t("width")}
            options={widthOptions}
            value={draft.width}
            onChange={handleWidthChange}
          />
        </div>
      </Card>

      <Card>
        <Text variant="headingMd" as="h3">
          {t("Display")}
        </Text>
        <div className="mt-4 flex flex-col gap-3">
          <Select
            label={t("themeLayoutBackdropDisplayCornerRadius")}
            options={cornerRadiusOptions}
            value={draft.corner_radius}
            onChange={handleCornerRadiusChange}
          />
        </div>
      </Card>
    </BlockStack>
  )
}

export default ThemeLayoutLeftStyleShowcaseTabPanel
